import { useState, useEffect } from 'react';
import { isPlatformSafari, isPlatformNodeJS } from '@utils/common';

const AUDIO_PLAYER = (() => {
  if (isPlatformNodeJS()) {
    return undefined;
  }
  const player = document.createElement("audio");
  document.body.appendChild(player);
  return player;
})();
const STOP_LISTENERS = new Set();
const useAudioPlayback = () => {
  const [src, setSrc] = useState(undefined);
  const [progress, setProgress] = useState(undefined);
  useEffect(() => {
    if (!AUDIO_PLAYER) {
      return;
    }
    if (src !== undefined) {
      // Pause all playing elements
      const players = document.querySelectorAll("audio");
      players.forEach(player => {
        if (player.src !== src) {
          player.pause();
        }
      });
      let progressAnimationFrame;
      const handleProgress = () => {
        if (AUDIO_PLAYER.src === src) {
          progressAnimationFrame = requestAnimationFrame(handleProgress);
          if (AUDIO_PLAYER.currentTime > 0 && AUDIO_PLAYER.duration > 0) {
            if (AUDIO_PLAYER.currentTime < AUDIO_PLAYER.duration) {
              setProgress(AUDIO_PLAYER.currentTime / AUDIO_PLAYER.duration);
            } else {
              handleStop();
            }
          }
        } else {
          handleStop();
        }
      };
      const handleStart = () => {
        progressAnimationFrame = requestAnimationFrame(handleProgress);
      };
      const handleStop = () => {
        setProgress(undefined);
        setSrc(undefined);
        cancelAnimationFrame(progressAnimationFrame);
        STOP_LISTENERS.forEach(listener => {
          listener();
        });
      };
      AUDIO_PLAYER.addEventListener("play", handleStart);
      AUDIO_PLAYER.addEventListener("pause", handleStop);
      AUDIO_PLAYER.addEventListener("ended", handleStop);
      return () => {
        AUDIO_PLAYER.removeEventListener("play", handleStart);
        AUDIO_PLAYER.removeEventListener("pause", handleStop);
        AUDIO_PLAYER.removeEventListener("ended", handleStop);
      };
    }
  }, [src]);
  const start = async src => {
    if (!AUDIO_PLAYER) {
      return;
    }
    if (AUDIO_PLAYER.src !== src) {
      AUDIO_PLAYER.pause();
      if (isPlatformSafari()) {
        AUDIO_PLAYER.dispatchEvent(new Event("pause"));
      }
    }
    setSrc(src);
    setProgress(undefined);
    AUDIO_PLAYER.src = src;
    await AUDIO_PLAYER.play();
    if (isPlatformSafari()) {
      AUDIO_PLAYER.dispatchEvent(new Event("play"));
    }
    setProgress(0);
  };
  const stop = () => {
    if (!AUDIO_PLAYER) {
      return;
    }
    if (AUDIO_PLAYER.src === src) {
      AUDIO_PLAYER.pause();
      AUDIO_PLAYER.src = "";
      if (isPlatformSafari()) {
        AUDIO_PLAYER.dispatchEvent(new Event("pause"));
      }
      STOP_LISTENERS.forEach(listener => {
        listener();
      });
    }
  };
  return {
    progress,
    start,
    stop
  };
};
const stopAudioPlayback = () => {
  if (!AUDIO_PLAYER) {
    return;
  }
  AUDIO_PLAYER.pause();
  if (isPlatformSafari()) {
    AUDIO_PLAYER.dispatchEvent(new Event("play"));
  }
  AUDIO_PLAYER.src = "";
  STOP_LISTENERS.forEach(listener => {
    listener();
  });
};
const addAudioPlaybackStopListener = listener => {
  STOP_LISTENERS.add(listener);
};
const removeAudioPlaybackStopListener = listener => {
  STOP_LISTENERS.delete(listener);
};

export { addAudioPlaybackStopListener, removeAudioPlaybackStopListener, stopAudioPlayback, useAudioPlayback };
//# sourceMappingURL=useAudioPlayback.js.map
